import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mergeChurchFeatures, type ChurchFeaturesStored } from "@heylife/core";
import { api } from "../api";
import { initialFeatureSave, noteFeatureEdit, noteFeatureSaveFinish, noteFeatureSaveStart } from "../featureSave";
import { useT } from "../i18n";
import { Msg, Toggle, useFlash } from "../ui";

type FeaturesPayload = {
  stored: ChurchFeaturesStored;
  effective: {
    admin: Record<string, boolean>;
    contentTabs: Record<string, boolean>;
    mobile: { tabs: Record<string, boolean>; me: Record<string, boolean> };
    app: Record<string, boolean>;
  };
  catalog: {
    admin: string[];
    contentTabs: string[];
    mobileTabs: string[];
    mobileMe: string[];
    app: string[];
  };
};

export function FeatureMatrix({ churchId, churchName }: { churchId: string; churchName: string }) {
  const { t } = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "churches", churchId, "features"], queryFn: () => api<FeaturesPayload>(`/admin/churches/${churchId}/features`) });
  const [draft, setDraft] = useState<ChurchFeaturesStored | null>(null);
  const [flash, run, busy] = useFlash();
  const queueRef = useRef(initialFeatureSave());
  const seenChurch = useRef(churchId);
  if (seenChurch.current !== churchId) {
    seenChurch.current = churchId;
    queueRef.current = initialFeatureSave();
    if (draft) setDraft(null);
  }

  const stored = mergeChurchFeatures(q.data?.stored ?? {}, draft ?? {});
  const effective = useMemo(() => {
    if (!q.data) return null;
    const merge = (base: Record<string, boolean>, patch?: Record<string, boolean>) => {
      const out = { ...base };
      if (patch) for (const [k, v] of Object.entries(patch)) out[k] = v;
      return out;
    };
    return {
      admin: merge(q.data.effective.admin, draft?.admin),
      contentTabs: merge(q.data.effective.contentTabs, draft?.contentTabs),
      mobile: {
        tabs: merge(q.data.effective.mobile.tabs, draft?.mobile?.tabs),
        me: merge(q.data.effective.mobile.me, draft?.mobile?.me),
      },
      app: merge(q.data.effective.app, draft?.app),
    };
  }, [q.data, draft]);

  const setFlag = (section: "admin" | "contentTabs" | "app", k: string, on: boolean) =>
    setDraft((d) => ({ ...(d ?? {}), [section]: { ...d?.[section], [k]: on } }));
  const setMobileFlag = (group: "tabs" | "me", k: string, on: boolean) =>
    setDraft((d) => ({ ...(d ?? {}), mobile: { ...d?.mobile, [group]: { ...d?.mobile?.[group], [k]: on } } }));

  const dirty = draft !== null;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const timerRef = useRef<number | null>(null);
  const churchIdRef = useRef(churchId);
  churchIdRef.current = churchId;
  const tRef = useRef(t);
  tRef.current = t;

  const saveMut = useMutation({
    mutationFn: (payload: ChurchFeaturesStored) => api(`/admin/churches/${churchIdRef.current}/features`, { method: "PATCH", json: payload }),
  });

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const armTimer = () => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void flush();
    }, 500);
  };

  /** Send the dirty keys only. The API merges them onto the locked church row. */
  const flush = async () => {
    const patch = draftRef.current;
    if (!patch || queueRef.current.inFlightGen !== null) return;
    const savedGen = queueRef.current.gen;
    const churchAtStart = churchIdRef.current;
    queueRef.current = noteFeatureSaveStart(queueRef.current);
    const ok = await run(() => saveMut.mutateAsync(patch), tRef.current("admin.features.saved"));
    if (churchIdRef.current !== churchAtStart) {
      if (queueRef.current.inFlightGen !== null) queueRef.current = { ...queueRef.current, inFlightGen: null };
      return;
    }
    const done = noteFeatureSaveFinish(queueRef.current, savedGen);
    queueRef.current = done.queue;
    if (!ok) return;
    if (done.clearDraft) {
      setDraft(null);
      await qc.invalidateQueries({ queryKey: ["admin", "churches", churchIdRef.current, "features"] });
      await qc.invalidateQueries({ queryKey: ["admin", "capabilities"] });
      return;
    }
    if (done.armTimer) armTimer();
  };

  const save = () => {
    if (!draftRef.current) return;
    clearTimer();
    void flush();
  };

  /** Toggles only lived in local state until Save — refresh dropped them. Auto-save after each change. */
  useEffect(() => {
    if (!draft) return;
    const next = noteFeatureEdit(queueRef.current);
    queueRef.current = next.queue;
    if (next.armTimer) armTimer();
    return () => clearTimer();
  }, [draft]);

  const catalog = q.data?.catalog;
  const appSections = useMemo(() => groupAppFeatures(catalog?.app ?? []), [catalog?.app]);

  if (q.isLoading) return <p className="muted">{t("admin.common.loading")}</p>;
  if (q.isError || !catalog) return <p className="error">{t("admin.common.error")}</p>;

  return (
    <div className="feature-matrix inner">
      <h3>{t("admin.features.title", { church: churchName })}</h3>
      <p className="muted">{t("admin.features.hint")}</p>

      <MatrixSection title={t("admin.features.adminSection")}>
        {catalog.admin.map((k) => (
          <ToggleRow key={k} label={t(`admin.nav.${k}`)} on={effective?.admin[k] ?? true} stored={storedFlag(stored.admin, k)} onChange={(on) => setFlag("admin", k, on)} />
        ))}
      </MatrixSection>

      <MatrixSection title={t("admin.features.contentSection")}>
        {catalog.contentTabs.map((k) => (
          <ToggleRow key={k} label={t(`admin.content.tabs.${k}`)} on={effective?.contentTabs[k] ?? true} stored={storedFlag(stored.contentTabs, k)} onChange={(on) => setFlag("contentTabs", k, on)} />
        ))}
      </MatrixSection>

      <MatrixSection title={t("admin.features.mobileTabsSection")}>
        {catalog.mobileTabs.map((k) => (
          <ToggleRow key={k} label={t(`tabs.${k}`)} on={effective?.mobile.tabs[k] ?? true} stored={storedFlag(stored.mobile?.tabs, k)} onChange={(on) => setMobileFlag("tabs", k, on)} />
        ))}
      </MatrixSection>

      <MatrixSection title={t("admin.features.mobileMeSection")}>
        {catalog.mobileMe.map((k) => (
          <ToggleRow key={k} label={t(`admin.features.me.${k}`)} on={effective?.mobile.me[k] ?? true} stored={storedFlag(stored.mobile?.me, k)} onChange={(on) => setMobileFlag("me", k, on)} />
        ))}
      </MatrixSection>

      {appSections.map(([section, keys]) => (
        <MatrixSection key={section} title={t(`admin.features.appSections.${section}`)}>
          {keys.map((k) => (
            <ToggleRow key={k} label={t(`admin.features.app.${k}`)} on={effective?.app[k] ?? true} stored={storedFlag(stored.app, k)} onChange={(on) => setFlag("app", k, on)} />
          ))}
        </MatrixSection>
      ))}

      <div className="actions">
        <button className="btn small" disabled={!dirty || busy} onClick={save}>
          {t("admin.common.save")}
        </button>
        <button type="button" className="btn small ghost" disabled={!dirty} onClick={() => setDraft(null)}>
          {t("admin.common.cancel")}
        </button>
      </div>
      <Msg msg={flash} />
    </div>
  );
}

function MatrixSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="panel matrix-section">
      <h4>{title}</h4>
      <div className="matrix-rows">{children}</div>
    </section>
  );
}

function storedFlag(section: object | undefined, key: string): boolean | undefined {
  if (!section) return undefined;
  return (section as Record<string, boolean | undefined>)[key];
}

function ToggleRow({ label, on, stored, onChange }: { label: string; on: boolean; stored?: boolean; onChange: (on: boolean) => void }) {
  const changed = stored !== undefined && stored !== on;
  return (
    <div className={`matrix-row ${changed ? "changed" : ""}`}>
      <span>{label}</span>
      <Toggle label="" checked={on} onChange={onChange} />
    </div>
  );
}

function groupAppFeatures(keys: string[]): Array<[string, string[]]> {
  const sections: Record<string, string[]> = {
    onboarding: [],
    home: [],
    challenges: [],
    community: [],
    me: [],
    leadership: [],
  };
  const map: Record<string, string> = {
    guestMode: "onboarding",
    accountCreate: "onboarding",
    myChurch: "onboarding",
    languageQuiet: "onboarding",
    notifications: "onboarding",
    dailyWord: "home",
    celebration: "home",
    activeQuest: "home",
    balanceRing: "home",
    oneThingToday: "home",
    followUpPrompt: "home",
    challengesBrowse: "challenges",
    challengeCheckIn: "challenges",
    cohortChat: "challenges",
    challengeShareCard: "challenges",
    stayTogetherFam: "challenges",
    feed: "community",
    prayerWallRead: "community",
    prayerShare: "community",
    prayerComments: "community",
    famChat: "community",
    directMessages: "community",
    famIdeas: "community",
    certificates: "me",
    gym: "me",
    journal: "me",
    heyFam: "me",
    myTeam: "me",
    prayFor: "me",
    invest: "me",
    talkSomeone: "me",
    faithCheck: "me",
    careView: "leadership",
    decisionsTake: "leadership",
    leadFam: "leadership",
    commandCenter: "leadership",
  };
  for (const k of keys) {
    const key = (map[k] ?? "me") as keyof typeof sections;
    sections[key]!.push(k);
  }
  return Object.entries(sections).filter(([, v]) => v.length);
}
